<!-- Products Table with Selection and Actions -->
@if (data) {
  <div class="products-table">
    <!-- Main Data Table -->
    <p-table
      #productsTable
      [value]="data.Items || []"
      [loading]="loading"
      [paginator]="true"
      [rows]="data.PageSize || 10"
      [totalRecords]="data.TotalRecords || 0"
      [lazy]="true"
      [sortMode]="'single'"
      [showCurrentPageReport]="true"
      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} products"
      [rowsPerPageOptions]="pageSizeOptions"
      [scrollHeight]="scrollHeight"
      [scrollable]="true"
      scrollDirection="both"
      size="small"
      [responsiveLayout]="responsiveLayout"
      [breakpoint]="breakpoint"
      dataKey="Id"
      [rowExpandMode]="'single'"
      [expandedRowKeys]="expandedRows"
      [(selection)]="selectedItems"
      (selectionChange)="onSelectionChange()"
      (onSort)="onSort($event)"
      (onPage)="onPageChange($event)"
      (onRowDblClick)="onRowDoubleClick($any($event))"
      (onRowExpand)="onRowExpand($event)"
      (onRowCollapse)="onRowCollapse($event)"
    >
      <!-- Table Header -->
      <ng-template pTemplate="header">
        <tr>
          <!-- Expand/Collapse Column (Frozen Left) -->
          <th
            style="width: 2rem"
            class="text-center"
            pFrozenColumn
            [frozen]="true"
            alignFrozen="left"
          >
            <span class="sr-only">Expand</span>
          </th>

          <!-- Checkbox Column (Frozen Left) -->
          @if (showCheckboxes) {
            <th
              style="width: 2rem"
              pFrozenColumn
              [frozen]="true"
              alignFrozen="left"
            >
              <p-checkbox
                [binary]="true"
                [ngModel]="isAllSelected()"
                [indeterminate]="isSomeSelected()"
                (ngModelChange)="toggleSelectAll()"
                [disabled]="loading || !data?.Items?.length"
              />
            </th>
          }

          <!-- ID Column (Frozen Left) -->
          <th
            pFrozenColumn
            [frozen]="true"
            alignFrozen="left"
            [pSortableColumn]="'ExternalId'"
            class="sortable-column"
            style="width: 4.5rem"
          >
            ID
            <p-sortIcon field="ExternalId" />
          </th>

          <!-- Product Name Column -->
          <th
            [pSortableColumn]="'ProductName'"
            class="sortable-column"
            style="min-width: 100px; max-width: 150px"
          >
            Product
            <p-sortIcon field="ProductName" />
          </th>

          <!-- Type Column -->
          <th
            [pSortableColumn]="'ProductType'"
            class="sortable-column"
            style="width: 60px"
          >
            Type
            <p-sortIcon field="ProductType" />
          </th>

          <!-- Stock Column -->
          <th
            [pSortableColumn]="'StockLevel'"
            class="sortable-column"
            style="min-width: 105px"
          >
            Stock
            <p-sortIcon field="StockLevel" />
          </th>

          <!-- Shipping Class Column -->
          <th
            [pSortableColumn]="'ShippingClass'"
            class="sortable-column"
            style="width: 70px"
          >
            Ship class
            <p-sortIcon field="ShippingClass" />
          </th>

          <!-- Lifetime Value Column -->
          <th
            [pSortableColumn]="'LifetimeValue'"
            class="sortable-column"
            style="width: 70px"
          >
            LTV
            <p-sortIcon field="LifetimeValue" />
          </th>

          <!-- Quantity Scheduled Column -->
          <th
            [pSortableColumn]="'QuantityScheduled'"
            class="sortable-column"
            style="width: 55px"
          >
            Qty sched
            <p-sortIcon field="QuantityScheduled" />
          </th>

          <!-- Availability Column -->
          <th style="width: 150px">Availability</th>

          <!-- Active Column -->
          <th
            [pSortableColumn]="'TotalQuantityScheduledActive'"
            class="sortable-column"
            style="width: 50px"
          >
            Active
            <p-sortIcon field="TotalQuantityScheduledActive" />
          </th>

          <!-- Paused Column -->
          <th
            [pSortableColumn]="'TotalQuantityScheduledPaused'"
            class="sortable-column"
            style="width: 50px"
          >
            Paused
            <p-sortIcon field="TotalQuantityScheduledPaused" />
          </th>

          <!-- Failed Column -->
          <th
            [pSortableColumn]="'TotalQuantityFailed'"
            class="sortable-column"
            style="width: 50px"
          >
            Failed
            <p-sortIcon field="TotalQuantityFailed" />
          </th>

          <!-- Sync Status Column -->
          <th style="width: 50px">Sync</th>

          <!-- Actions Column (Frozen Right) -->
          @if (showActions) {
            <th
              style="width: 3rem"
              pFrozenColumn
              [frozen]="true"
              alignFrozen="right"
            >
              Actions
            </th>
          }
        </tr>
      </ng-template>

      <!-- Table Body -->
      <ng-template
        pTemplate="body"
        let-product
        let-rowIndex="rowIndex"
        let-expanded="expanded"
      >
        <!-- Desktop Row -->
        <tr
          class="product-row desktop-row"
          [ngClass]="{ 'selected-row': isProductSelected(product) }"
          (click)="toggleRowExpansion(product)"
        >
          <!-- Row Expansion Toggle (Frozen Left) -->
          <td
            class="text-center expand-column"
            pFrozenColumn
            [frozen]="true"
            alignFrozen="left"
            (click)="$event.stopPropagation()"
          >
            <span class="p-column-title">Expand</span>
            <p-button
              [pRowToggler]="product"
              type="button"
              pRipple
              [text]="true"
              [rounded]="true"
              [plain]="true"
              size="small"
              styleClass="text-gray-400 hover:text-gray-600"
              [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
              [disabled]="loading"
            />
          </td>

          <!-- Checkbox (Frozen Left) -->
          @if (showCheckboxes) {
            <td
              class="checkbox-column"
              pFrozenColumn
              [frozen]="true"
              alignFrozen="left"
              (click)="$event.stopPropagation()"
            >
              <span class="p-column-title">Select</span>
              <p-checkbox
                [value]="product"
                [(ngModel)]="selectedItems"
                [binary]="false"
                (onChange)="onSelectionChange()"
                [disabled]="loading"
              />
            </td>
          }

          <!-- ID Column (Frozen Left) -->
          <td
            class="id-column"
            pFrozenColumn
            [frozen]="true"
            alignFrozen="left"
          >
            <span class="p-column-title">ID</span>
            <a
              href="javascript:;"
              class="id-link"
              (click)="onViewProduct(product); $event.stopPropagation()"
            >
              {{ product.ExternalId }}
            </a>
          </td>

          <!-- Product Name -->
          <td class="product-name-column">
            <span class="p-column-title">Product</span>
            <span
              class="product-name"
              [pTooltip]="product.ProductName"
              tooltipPosition="top"
            >
              {{ product.ProductName }}
            </span>
          </td>

          <!-- Product Type -->
          <td>
            <span class="p-column-title">Type</span>
            @if (product.ProductType) {
              <p-tag
                [value]="product.ProductType"
                severity="secondary"
                [rounded]="true"
                styleClass="text-xs"
              />
            } @else {
              <span class="type-text">-</span>
            }
          </td>

          <!-- Stock -->
          <td class="stock-column">
            <span class="p-column-title">Stock</span>
            <p-tag
              [value]="formatStockLevel(product.StockLevel)"
              [severity]="getStockSeverity(product)"
              [rounded]="true"
              styleClass="text-xs stock-tag-nowrap"
            />
          </td>

          <!-- Shipping Class -->
          <td>
            <span class="p-column-title">Shipping Class</span>
            <span class="shipping-class-text">{{
              product.ShippingClass || "-"
            }}</span>
          </td>

          <!-- Lifetime Value -->
          <td>
            <span class="p-column-title">LTV</span>
            <span class="price-text">{{
              formatPrice(product.LifetimeValue)
            }}</span>
          </td>

          <!-- Quantity Scheduled -->
          <td>
            <span class="p-column-title">Qty Scheduled</span>
            <span
              class="qty-text"
              [ngClass]="{ 'qty-highlight': product.QuantityScheduled > 0 }"
            >
              {{ product.QuantityScheduled ?? "N/A" }}
            </span>
          </td>

          <!-- Availability -->
          <td class="availability-cell" (click)="$event.stopPropagation()">
            <span class="p-column-title">Availability</span>
            @if (isUpdatingAvailability(product)) {
              <div class="availability-loading">
                <i class="pi pi-spin pi-spinner text-primary"></i>
                <span class="availability-loading-text">Updating...</span>
              </div>
            } @else {
              <div class="availability-checkboxes">
                <div class="availability-checkbox-item">
                  <p-checkbox
                    [binary]="true"
                    [ngModel]="hasAddToScheduledOrder(product)"
                    (ngModelChange)="
                      onAddToScheduledOrderChange(product, $event)
                    "
                    [disabled]="loading"
                    inputId="add-{{ product.Id }}"
                  />
                  <label for="add-{{ product.Id }}" class="availability-label">
                    Add To Scheduled Order
                  </label>
                </div>
                <div class="availability-checkbox-item">
                  <p-checkbox
                    [binary]="true"
                    [ngModel]="hasProcessScheduledOrder(product)"
                    (ngModelChange)="
                      onProcessScheduledOrderChange(product, $event)
                    "
                    [disabled]="loading"
                    inputId="process-{{ product.Id }}"
                  />
                  <label
                    for="process-{{ product.Id }}"
                    class="availability-label"
                  >
                    Process Scheduled Order
                  </label>
                </div>
              </div>
            }
          </td>

          <!-- Active -->
          <td class="text-center">
            <span class="p-column-title">Active</span>
            <span class="count-text">{{
              product.TotalQuantityScheduledActive ?? "N/A"
            }}</span>
          </td>

          <!-- Paused -->
          <td class="text-center">
            <span class="p-column-title">Paused</span>
            <span class="count-text">{{
              product.TotalQuantityScheduledPaused ?? "N/A"
            }}</span>
          </td>

          <!-- Failed -->
          <td class="text-center">
            <span class="p-column-title">Failed</span>
            <span
              class="count-text"
              [ngClass]="{
                'failed-highlight': product.TotalQuantityFailed > 0,
              }"
            >
              {{ product.TotalQuantityFailed ?? "N/A" }}
            </span>
          </td>

          <!-- Sync Status -->
          <td class="sync-status-cell">
            <span class="p-column-title">Sync Status</span>
            <div
              class="sync-status-wrapper"
              [pTooltip]="getSyncStatusMessage(product)"
              tooltipPosition="top"
            >
              @if (isSyncing(product)) {
                <i class="pi pi-cloud text-green-500"></i>
              } @else {
                <i class="pi pi-cloud-upload text-gray-400"></i>
              }
            </div>
          </td>

          <!-- Actions (Frozen Right) -->
          @if (showActions) {
            <td
              class="actions-column"
              pFrozenColumn
              [frozen]="true"
              alignFrozen="right"
              (click)="$event.stopPropagation()"
            >
              <span class="p-column-title">Actions</span>
              <p-button
                icon="pi pi-ellipsis-v"
                [text]="true"
                size="small"
                [disabled]="loading"
                (onClick)="openContextMenu($event, product, contextMenu)"
              />
            </td>
          }
        </tr>

        <!-- Mobile Card Row -->
        <tr
          class="mobile-card-row"
          [ngClass]="{ 'selected-row': isProductSelected(product) }"
        >
          <td [attr.colspan]="getColumnCount()">
            <app-product-card
              [product]="product"
              [selected]="isProductSelected(product)"
              [loading]="loading"
              [showCheckbox]="showCheckboxes"
              [expanded]="expanded"
              (selectionChange)="onMobileCardSelectionChange(product, $event)"
              (cardAction)="onMobileCardAction($event)"
              (cardClick)="onMobileCardClick($event)"
            />
          </td>
        </tr>
      </ng-template>

      <!-- Row Expansion Template -->
      <ng-template pTemplate="expandedrow" let-product>
        <tr class="expanded-row">
          <td [attr.colspan]="getColumnCount()" class="expanded-row-cell">
            <app-product-expanded-row
              [product]="product"
              [loading]="loading"
              (rowAction)="onExpandedRowAction($event)"
            />
          </td>
        </tr>
      </ng-template>

      <!-- Empty Message -->
      <ng-template pTemplate="emptymessage">
        <tr>
          <td [attr.colspan]="getColumnCount()" class="text-center py-8">
            <div class="empty-state">
              <i class="pi pi-box text-4xl text-gray-300 mb-3"></i>
              <p class="text-gray-500 mb-0">No products found</p>
              <p class="text-gray-400 text-sm">
                Try adjusting your filters or search criteria
              </p>
            </div>
          </td>
        </tr>
      </ng-template>

      <!-- Loading Body Template -->
      <ng-template pTemplate="loadingbody">
        <app-table-skeleton-rows [rows]="5" [columns]="skeletonColumns" />
      </ng-template>
    </p-table>
  </div>
}

<!-- Context Menu for Row Actions -->
<p-menu
  #contextMenu
  [model]="contextMenuItems"
  [popup]="true"
  appendTo="body"
/>
